export type PaymentReleaseSubMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  paymentRequestStatus?: string;
  projectInvoiceId?: string;
  isVariationOrder?: boolean;
  metadata?: { isVariationOrder?: boolean } | null;
};

export type PaymentReleaseMilestoneSelection = {
  id: string;
  name: string;
  budgetAmount?: number;
  projectInvoiceId?: string;
  subMilestones?: PaymentReleaseSubMilestoneSelection[] | null;
};

export type PaymentReleaseRequestToSend =
  | { kind: "milestone"; milestoneId: string; name: string }
  | { kind: "sub_milestone_batch"; milestoneId: string; subMilestones: PaymentReleaseSubMilestoneSelection[] };

function isSubPaymentApproved(sub: PaymentReleaseSubMilestoneSelection): boolean {
  return String(sub.paymentRequestStatus ?? "").toLowerCase() === "approved";
}

function getUnreleasedSubs(milestone: PaymentReleaseMilestoneSelection): PaymentReleaseSubMilestoneSelection[] {
  return (milestone.subMilestones ?? []).filter((sub) => !isSubPaymentApproved(sub));
}

function isOnSameInvoiceAsMilestone(sub: PaymentReleaseSubMilestoneSelection, milestone: PaymentReleaseMilestoneSelection): boolean {
  return !sub.projectInvoiceId || sub.projectInvoiceId === milestone.projectInvoiceId;
}

function isVariationOrderSub(sub: PaymentReleaseSubMilestoneSelection): boolean {
  return sub.isVariationOrder === true || sub.metadata?.isVariationOrder === true;
}

function toBatchSub(sub: PaymentReleaseSubMilestoneSelection, includeStatus = false): PaymentReleaseSubMilestoneSelection {
  return {
    id: sub.id,
    name: sub.name,
    budgetAmount: sub.budgetAmount,
    ...(includeStatus && sub.paymentRequestStatus != null ? { paymentRequestStatus: sub.paymentRequestStatus } : {}),
  };
}

/**
 * Builds payment-release API payloads from selected milestone/sub-milestone ids.
 * When the parent milestone is selected, its same-invoice subs are folded into a single
 * milestone request — the backend marks those subs pending too, so the client sees them
 * under that one request instead of getting a separate sub-milestone request. Subs on a
 * different invoice than the parent always need their own sub-milestone batch request.
 * A variation is never folded into the parent: it is sent only when its own checkbox is
 * selected, as its own batch, even when the parent is selected too.
 */
export function buildPaymentReleaseRequestsToSend(
  selectedItemIds: Iterable<string>,
  milestones: PaymentReleaseMilestoneSelection[]
): PaymentReleaseRequestToSend[] {
  const selected = new Set(selectedItemIds);
  const result: PaymentReleaseRequestToSend[] = [];

  for (const milestone of milestones) {
    const parentSelected = selected.has(milestone.id);
    const subs = milestone.subMilestones ?? [];
    const selectedSubs = subs.filter((sub) => selected.has(sub.id));
    const selectedVariations = selectedSubs.filter(isVariationOrderSub);
    const selectedRegular = selectedSubs.filter((sub) => !isVariationOrderSub(sub));
    const regularMilestone: PaymentReleaseMilestoneSelection = {
      ...milestone,
      subMilestones: subs.filter((sub) => !isVariationOrderSub(sub)),
    };

    if (selectedRegular.length > 0) {
      if (parentSelected) {
        result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
        const crossInvoiceSubs = selectedRegular.filter((sub) => !isOnSameInvoiceAsMilestone(sub, milestone));
        if (crossInvoiceSubs.length > 0) {
          result.push({
            kind: "sub_milestone_batch",
            milestoneId: milestone.id,
            subMilestones: crossInvoiceSubs.map((sub) => toBatchSub(sub)),
          });
        }
      } else {
        result.push({
          kind: "sub_milestone_batch",
          milestoneId: milestone.id,
          subMilestones: selectedRegular.map((sub) => toBatchSub(sub)),
        });
      }
    } else if (parentSelected) {
      const unreleasedSubs = getUnreleasedSubs(regularMilestone);
      if (unreleasedSubs.length > 0) {
        result.push({
          kind: "sub_milestone_batch",
          milestoneId: milestone.id,
          subMilestones: unreleasedSubs.map((sub) => toBatchSub(sub, true)),
        });
      } else {
        result.push({ kind: "milestone", milestoneId: milestone.id, name: milestone.name });
      }
    }

    if (selectedVariations.length > 0) {
      result.push({
        kind: "sub_milestone_batch",
        milestoneId: milestone.id,
        subMilestones: selectedVariations.map((sub) => toBatchSub(sub)),
      });
    }
  }

  return result;
}
